import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import SecurityWarning from './components/SecurityWarning';
import SEOManager from './components/SEOManager';
import ErrorBoundary from './components/ErrorBoundary';
import OptimizedLoadingSpinner from './components/OptimizedLoadingSpinner';

// Import security framework
import { SecurityUtils } from './utils/security';
import { keyManager } from './utils/keyManager';
import { firebaseConfig } from './firebase/config';

import { CollegeProvider } from './context/CollegeContext';
import { ScholarshipProvider } from './context/ScholarshipContext';
import { StudentProvider } from './context/StudentContext';
import { AuthProvider } from './context/AuthContext';
import { NewsProvider } from './context/NewsContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import StudentProtectedRoute from './components/StudentProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';

// Lazy load all page components
const HomePage = lazy(() => import('./pages/HomePage'));
const MBBSPage = lazy(() => import('./pages/MBBSPage'));
const BDSPage = lazy(() => import('./pages/BDSPage'));
const VeterinaryPage = lazy(() => import('./pages/VeterinaryPage'));
const PharmacyPage = lazy(() => import('./pages/PharmacyPage'));
const NursingPage = lazy(() => import('./pages/NursingPage'));
const AlliedHealthSciencePage = lazy(() => import('./pages/AlliedHealthSciencePage'));
const ArtsAndSciencePage = lazy(() => import('./pages/ArtsAndSciencePage'));
const EngineeringPage = lazy(() => import('./pages/EngineeringPage'));
const PolytechnicPage = lazy(() => import('./pages/PolytechnicPage'));
const CollegesPage = lazy(() => import('./pages/CollegesPage'));
const PaidConsultantPage = lazy(() => import('./pages/PaidConsultantPage'));
const ConsultancyPage = lazy(() => import('./pages/ConsultancyPage'));
const AbroadConsultancyForm = lazy(() => import('./pages/AbroadConsultancyForm'));
const AbroadStudiesPage = lazy(() => import('./pages/AbroadStudiesPage'));
const ScholarshipsPage = lazy(() => import('./pages/ScholarshipsPage'));
const ScholarshipApplicationPage = lazy(() => import('./pages/ScholarshipApplicationPage'));
const InternshipPartnershipsPage = lazy(() => import('./pages/InternshipPartnershipsPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const ConsultancyLoginPage = lazy(() => import('./pages/ConsultancyLoginPage'));
const ConsultancyDashboard = lazy(() => import('./pages/ConsultancyDashboard'));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const AdminNewsPage = lazy(() => import('./pages/AdminNewsPage'));
const AdminNewsLogin = lazy(() => import('./pages/AdminNewsLogin'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
import CoursePageLoader from './pages/CoursePageLoader';

function App() {
  // Initialize security framework
  useEffect(() => {
    const initializeSecurity = async () => {
      try {
        // Initialize security systems
        console.log('🛡️ Initializing security framework...');
        
        // Initialize key manager
        keyManager.initializeKeys();
        
        // Initialize Firebase with secure configuration
        await firebaseConfig.initialize();
        
        // Initialize OWASP security framework
        SecurityUtils.initialize();
        
        // Run security audit
        const audit = SecurityUtils.componentSecurity.checkSecurityStatus();
        if (audit.secure) {
          console.log('✅ Security audit passed');
        } else {
          console.warn('⚠️ Security issues detected:', audit.issues);
        }
        
        console.log('🔒 Security framework initialization complete');
      } catch (error) {
        console.error('❌ Security initialization failed:', error);
      }
    };
    
    initializeSecurity();
  }, []);

  // Dev-only mount log to confirm React reached App component
  if (process.env.NODE_ENV === 'development') {
    console.log('APP.TSX: React render reached App component');
  }
  const LoadingSpinner = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <OptimizedLoadingSpinner />
    </div>
  );

  return (
    <ErrorBoundary>
      <CollegeProvider>
        <ScholarshipProvider>
          <StudentProvider>
            <AuthProvider>
              <AdminAuthProvider>
                <NewsProvider>
                  <Router>
                  <SEOManager />
                  <div className="min-h-screen bg-gray-50">
                    <SecurityWarning />
                    <Header />
                    <main>
                      <Suspense fallback={<LoadingSpinner />}>
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          {/* ...existing routes... */}
                          <Route path="/mbbs" element={<MBBSPage />} />
                        <Route path="/bds" element={<BDSPage />} />
                        <Route path="/veterinary" element={<VeterinaryPage />} />
                        <Route path="/pharmacy" element={<PharmacyPage />} />
                        <Route path="/nursing" element={<NursingPage />} />
                        <Route path="/allied-health" element={<AlliedHealthSciencePage />} />
                        <Route path="/arts-science" element={<ArtsAndSciencePage />} />
                        <Route path="/engineering" element={<EngineeringPage />} />
                        <Route path="/polytechnic" element={<PolytechnicPage />} />
                        <Route path="/colleges" element={<CollegesPage />} />
                        <Route path="/premium-services" element={<PaidConsultantPage />} />
                        <Route path="/consultancy" element={<ConsultancyPage />} />
                        <Route path="/abroad-studies" element={<AbroadStudiesPage />} />
                        <Route path="/abroad-consultancy" element={<AbroadConsultancyForm />} />
                        <Route path="/scholarships" element={<ScholarshipsPage />} />
                        <Route path="/apply-scholarship/:scholarshipId" element={<ScholarshipApplicationPage />} />
                        <Route path="/internships-partnerships" element={<InternshipPartnershipsPage />} />
                        <Route path="/internships" element={<InternshipPartnershipsPage />} />
                        <Route path="/about-us" element={<AboutUsPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/admin-login" element={<AdminLogin />} />
                        <Route path="/consultancy-login" element={<ConsultancyLoginPage />} />
                        <Route path="/consultancy-dashboard" element={<ConsultancyDashboard />} />
                        <Route path="/student-dashboard" element={
                          <StudentProtectedRoute>
                            <StudentDashboard />
                          </StudentProtectedRoute>
                        } />
                        <Route path="/signup" element={<SignupPage />} />
                        <Route path="/admin/login" element={<AdminNewsLogin />} />
                        <Route path="/admin/news" element={
                          <AdminProtectedRoute>
                            <AdminNewsPage />
                          </AdminProtectedRoute>
                        } />
                        {/* dynamic loader for /courses/:slug pages */}
                        <Route path="/courses/:slug" element={<CoursePageLoader />} />
                        {/* ...course detail routes... */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                      {/* legacy fallback message removed to avoid showing a white box on all pages */}
                    </Suspense>
                  </main>
                  <Footer />
                </div>
              </Router>
              </NewsProvider>              </AdminAuthProvider>            </AuthProvider>
          </StudentProvider>
        </ScholarshipProvider>
      </CollegeProvider>
    </ErrorBoundary>
  );
}

export default App;